<script lang="ts">
    import { Dialog } from 'bits-ui';
    import { Button } from '@/components/ui/button';
    import { router } from '@inertiajs/svelte';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import Input from '@/components/Inputs/Input.svelte';
    import Textarea from '@/components/Inputs/Textarea.svelte';
    import Select from '@/components/Inputs/Select.svelte';
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import Checkbox from '@/components/Inputs/Checkbox.svelte';
    import type { Field } from '@/types/types';
    import type Blocks from '@/schemas/public/Blocks';
    import { parseFieldOptions } from '@/lib/fieldUtils';

    type User = {
        id: string;
        name: string;
    };

    type Message = {
        id?: string;
        title: string;
        body: string;
        priority: 'low' | 'medium' | 'high';
        assigned_to?: string;
    };

    type Props = {
        open: boolean;
        message?: Message;
        users: User[];
        blocks: (Blocks & { fields: Field[] })[];
        onClose: () => void;
    };

    let { open = $bindable(), message, users, blocks, onClose }: Props = $props();

    // Initialize form data from message or defaults
    let formData = $state<Record<string, any>>({});
    let lastOpen = false;

    $effect(() => {
        // Only reset when dialog opens (transition from false to true)
        if (open && !lastOpen) {
            formData = {};

            if (message) {
                // Populate from existing message
                Object.keys(message).forEach(key => {
                    formData[key] = message[key as keyof Message];
                });
            } else {
                // Set defaults from field definitions
                blocks.forEach(block => {
                    block.fields.forEach(field => {
                        if (field.default_value !== null && field.default_value !== undefined) {
                            formData[field.column_name] = field.default_value;
                        }
                    });
                });
            }
        }

        lastOpen = open;
    });

    // Convert users to options format
    const userOptions = $derived([
        { value: '', label: 'Everyone' },
        ...users.map(u => ({ value: u.id, label: u.name }))
    ]);

    function handleSubmit(e: Event) {
        e.preventDefault();

        if (message?.id) {
            // Update existing message
            router.put(`/messages/${message.id}`, formData, {
                onSuccess: () => {
                    onClose();
                },
            });
        } else {
            // Create new message
            router.post('/messages', formData, {
                onSuccess: () => {
                    onClose();
                },
            });
        }
    }
</script>

<Dialog.Root bind:open>
    <Dialog.Portal>
        <Dialog.Overlay
            class="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <Dialog.Content
            class="fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg max-h-[90vh] overflow-y-auto"
        >
            <Dialog.Title class="text-lg font-semibold leading-none tracking-tight">
                {message?.id ? 'Edit Message' : 'Create Message'}
            </Dialog.Title>
            <Dialog.Description class="text-sm text-muted-foreground">
                {message?.id ? 'Update message details' : 'Create a new informational message'}
            </Dialog.Description>

            <form on:submit={handleSubmit} class="space-y-4">
                {#each blocks as block (block.id)}
                    <FormBlock label={block.label}>
                        {#each block.fields as field (field.id)}
                            {#if field.editable !== false}
                                {#if field.ui_type === 'text'}
                                    <Input
                                        label={field.label}
                                        bind:value={formData[field.column_name]}
                                        required={field.required}
                                        placeholder={field.placeholder}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <div class="col-span-full">
                                        <Textarea
                                            label={field.label}
                                            bind:value={formData[field.column_name]}
                                            required={field.required}
                                            placeholder={field.placeholder}
                                        />
                                    </div>
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={formData[field.column_name]}
                                        options={parseFieldOptions(field)}
                                    />
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={formData[field.column_name]}
                                    />
                                {:else if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        label={field.label}
                                        bind:checked={formData[field.column_name]}
                                    />
                                {:else if field.ui_type === 'related' && field.column_name === 'assigned_to'}
                                    <Select
                                        label={field.label}
                                        bind:value={formData[field.column_name]}
                                        options={userOptions}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="flex justify-end gap-3">
                    <Button type="button" variant="outline" onclick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit">
                        {message?.id ? 'Update' : 'Send'} Message
                    </Button>
                </div>
            </form>
        </Dialog.Content>
    </Dialog.Portal>
</Dialog.Root>